import { useCallback } from 'react';
import validate, { Network, getAddressInfo } from 'bitcoin-address-validation';
import { isAddress } from 'ethers/lib/utils';
import { PublicKey } from '@solana/web3.js';
import { isValidSeiAddress } from '@sei-js/core';

export function useValidateAddr() {
  const validateBtcAddr = useCallback(
    (
      addr: string,
      onChange: (verifyAddress: number, value: string) => void,
      matchBtcType?: string
    ) => {
      if (validate(String(addr).trim())) {
        const addressInfo = getAddressInfo(String(addr).trim());
        if (
          matchBtcType === 'ALL' ||
          (matchBtcType !== 'ALL' &&
            addressInfo.type === 'p2tr' &&
            addressInfo.network === Network.mainnet)
        ) {
          onChange(1, String(addr).trim());
          return;
        }
        onChange(-1, String(addr).trim());
      } else {
        if (String(addr).trim().startsWith('bc1p') || matchBtcType === 'ALL') {
          onChange(-3, String(addr).trim());
        } else {
          onChange(-1, String(addr).trim());
        }
      }
    },
    []
  );

  const isEthAddr = useCallback((addr: string) => {
    return isAddress(addr?.trim());
  }, []);

  const isBtcAddress = useCallback((addr: string) => {
    if (validate(addr.trim())) {
      const addrInfo = getAddressInfo(addr.trim());
      if (addrInfo.type === 'p2tr' && addrInfo.network === Network.mainnet) {
        return true;
      }
      return false;
    }
    return false;
  }, []);

  const isSolanaAddr = useCallback((addr: string) => {
    try {
      let pubkey = new PublicKey(addr?.trim());
      let isSolana = PublicKey.isOnCurve(pubkey.toBuffer());
      return isSolana;
    } catch (error) {
      return false;
    }
  }, []);

  const isSeiAddr = useCallback((addr: string) => {
    return Boolean(isValidSeiAddress(addr?.trim()));
  }, []);

  const isINJAddr = useCallback((addr: string) => {
    return addr?.trim().startsWith('inj') && addr?.trim()?.length > 31;
  }, []);

  const isStarknetAddr = useCallback((addr: string) => {
    const regex = /^0x[0-9a-fA-F]{64}$/;
    return regex.test(addr);
  }, []);

  const isTonAddr = useCallback((addr: string) => {
    const regex = /^[EU]Q.{46}$/;
    return regex.test(addr);
  }, []);

  const isValidAddr = useCallback(
    (addr: string, chain: string) => {
      let check = isEthAddr(addr);
      switch (chain) {
        case 'BTC_Ordinal' || 'BRC20' || 'BTC':
          check = isBtcAddress(addr);
          break;
        case 'Solana':
          check = isSolanaAddr(addr);
          break;
        case 'Sei_Network':
          check = isSeiAddr(addr);
          break;
        case 'Injective':
          check = isINJAddr(addr);
          break;
        case 'Starknet':
          check = isStarknetAddr(addr);
          break;
        case 'Ton':
          check = isTonAddr(addr);
          break;

        case 'ALL':
          check =
            isEthAddr(addr) ||
            isBtcAddress(addr) ||
            isSolanaAddr(addr) ||
            isSeiAddr(addr) ||
            isINJAddr(addr) ||
            isStarknetAddr(addr) ||
            isTonAddr(addr);
      }
      return check;
    },
    [
      isBtcAddress,
      isEthAddr,
      isINJAddr,
      isSeiAddr,
      isSolanaAddr,
      isStarknetAddr,
      isTonAddr,
    ]
  );

  return {
    validateBtcAddr,
    isEthAddr,
    isBtcAddress,
    isSolanaAddr,
    isValidAddr,
  };
}
